import React from 'react';
import {ErrorBoundary as BaseErrorBoundary} from 'react-error-boundary';
import {styled} from '@netx/core-components/Theme';
import {faExclamationCircle} from '@fortawesome/pro-regular-svg-icons';

import {i18n} from '@netx/core/i18n';

import {
	EmptyStateFallbackMessage,
	type EmptyStateFallbackMessageProps,
} from '@netx/core-components/EmptyStateFallbackMessage';

const ErrorBoundaryRoot = styled(EmptyStateFallbackMessage)(({theme}) => ({
	width: '100%',
}));

export interface ErrorBoundaryProps extends React.PropsWithChildren<
	Omit<EmptyStateFallbackMessageProps, 'onActionButtonClick'>
> {}

/**
 * Wrapper for the error boundary class for easier passing of styles with theme support
 *
 * DEVNOTE: It is advised to place an error boundary at the highest level possible \
 * Due to the nature of our app we will currently be wrapping the highest level \
 * of our components as we can (without having a react wrapper around the whole app) \
 * As we move forward and make progress we can remove the wrapper from lower levels \
 * and keep moving up the tree until (one day) we get to the top of the app
 */
// TODO: convert this to styled and anything that wants to use it can just wrap in styled
// and we can do away with this `any` (`WithStyles` is deprecated and does not play well
// with ts)
const ErrorBoundaryPrimitive = ({children, ...props}: ErrorBoundaryProps) => {
	return (
		<BaseErrorBoundary
			fallbackRender={({error, resetErrorBoundary}) => (
				<ErrorBoundaryRoot
					icon={faExclamationCircle}
					title={i18n.t('i18n.errorBoundaryTitle')}
					message={i18n.t('i18n.errorBoundaryMessage')}
					{...props}
					actionButtonLabel={i18n.t('i18n.reload')}
					onActionButtonClick={() => {
						resetErrorBoundary();
					}}
				/>
			)}
		>
			{children}
		</BaseErrorBoundary>
	);
};

export const ErrorBoundary = ErrorBoundaryPrimitive;

export default ErrorBoundary;
